import React, { useState, useEffect } from 'react';
import { X, Save, Loader2, Shield } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const copy = {
  fr: {
    title: "Modifier l'utilisateur",
    firstName: "Prénom",
    lastName: "Nom",
    email: "Adresse courriel",
    phone: "Téléphone",
    role: "Rôle",
    user: "Utilisateur",
    admin: "Administrateur",
    cancel: "Annuler",
    save: "Enregistrer",
    saving: "Enregistrement…",
    success: "Utilisateur mis à jour avec succès.",
    genericError: "Une erreur s'est produite. Veuillez réessayer.",
  },
  en: {
    title: "Edit User",
    firstName: "First Name",
    lastName: "Last Name",
    email: "Email Address",
    phone: "Phone",
    role: "Role",
    user: "User",
    admin: "Admin",
    cancel: "Cancel",
    save: "Save Changes",
    saving: "Saving…",
    success: "User updated successfully.",
    genericError: "Something went wrong. Please try again.",
  },
};

export default function AdminEditUserModal({ open, onClose, user, token, language = 'fr', onSaved, currentUserRole = 'admin' }) {
  const t = copy[language] || copy.fr;
  const isSubAdmin = currentUserRole === 'sub_admin';
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', role: 'user' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (open && user) {
      setForm({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone: user.phone || '',
        role: user.role || 'user',
      });
      setError('');
      setSuccess('');
    }
  }, [open, user]);

  if (!open || !user) return null;

  const handleChange = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      // Sub-admins are not allowed to change roles — strip the field before sending.
      const payload = isSubAdmin ? { ...form } : form;
      if (isSubAdmin) delete payload.role;
      const res = await axios.patch(
        `${BACKEND_URL}/api/admin/users/${user.id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(t.success);
      if (onSaved) onSaved(res.data);
      setTimeout(() => onClose(), 1100);
    } catch (err) {
      setError(err?.response?.data?.detail || t.genericError);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="admin-edit-user-modal"
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-extrabold text-[#1D1D1D] flex items-center gap-2">
            <Shield className="w-4 h-4 text-[#CD0000]" /> {t.title}
          </h2>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4" data-testid="admin-edit-user-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1.5">{t.firstName}</label>
              <input
                data-testid="admin-edit-user-first-name"
                required value={form.first_name} onChange={handleChange('first_name')}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#CD0000]"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1.5">{t.lastName}</label>
              <input
                data-testid="admin-edit-user-last-name"
                required value={form.last_name} onChange={handleChange('last_name')}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#CD0000]"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1.5">{t.email}</label>
            <input
              data-testid="admin-edit-user-email"
              type="email" required value={form.email} onChange={handleChange('email')}
              className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#CD0000]"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1.5">{t.phone}</label>
            <input
              data-testid="admin-edit-user-phone"
              type="tel" value={form.phone} onChange={handleChange('phone')}
              className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#CD0000]"
            />
          </div>
          {!isSubAdmin && (
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1.5">{t.role}</label>
              <select
                data-testid="admin-edit-user-role"
                value={form.role} onChange={handleChange('role')}
                className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#CD0000] bg-white"
              >
                <option value="user">{t.user}</option>
                <option value="admin">{t.admin}</option>
              </select>
            </div>
          )}

          {error && <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>}
          {success && <p className="text-sm text-green-700 bg-green-50 border border-green-100 rounded-lg px-3 py-2">{success}</p>}

          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-2.5 rounded-xl transition">{t.cancel}</button>
            <button
              type="submit" disabled={saving}
              data-testid="admin-edit-user-submit"
              className="flex-1 bg-[#CD0000] hover:bg-[#A00000] disabled:opacity-60 text-white font-semibold py-2.5 rounded-xl transition flex items-center justify-center gap-2"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? t.saving : t.save}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
